import { useCallback, useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { api, fmtDate } from '../api';
import { useAuth } from '../AuthContext';
import { hasPlan, productPlanLabel } from '../utils/plans';
import {
  IconChat,
  IconCheck,
  IconClock,
  IconQrCode,
  IconRefresh,
  IconSend,
  IconShield,
  IconX,
  IconZap,
} from './Icons';

const TYPE_LABEL = {
  reminder: 'Recordatorio',
  renewal: 'Renovación',
  overdue: 'Cobro vencido',
  account_delivery: 'Entrega de cuenta',
  test: 'Prueba',
  manual: 'Manual',
};

const STATUS_COPY = {
  idle: ['Sin vincular', 'Conecta el número desde el que saldrán tus mensajes.'],
  connecting: ['Conectando', 'Estamos preparando la conexión con WhatsApp.'],
  qr: ['Escanea el QR', 'Abre WhatsApp → Dispositivos vinculados → Vincular dispositivo.'],
  pairing: ['QR aceptado', 'Estamos guardando la sesión y terminando el vínculo. No cierres esta pantalla.'],
  connected: ['Conectado', 'El canal está listo para enviar mensajes.'],
  reconnecting: ['Reconectando', 'Restableciendo el canal automáticamente.'],
  disconnected: ['Desconectado', 'La sesión está guardada; vuelve a conectar el canal.'],
  expired: ['QR vencido', 'El código anterior ya no sirve. Genera uno nuevo para continuar.'],
  logged_out: ['Sesión cerrada', 'Vincula nuevamente el número mediante QR.'],
  replaced: ['Conexión reemplazada', 'Otro dispositivo tomó esta sesión. Vuelve a vincularla.'],
  error: ['Requiere atención', 'No pudimos completar la conexión.'],
};

const templateSamples = {
  cliente: 'María',
  servicio: 'Netflix Premium',
  fecha: '24 de julio de 2026',
  monto: '₡3,500',
  correo: 'cliente@stream.com',
  contrasena: 'CuentaSegura25',
  perfil: 'María',
  pin: '2468',
  negocio: 'VisionPlay',
};

function previewTemplate(template) {
  return Object.entries(templateSamples).reduce(
    (message, [key, value]) => message.replaceAll(`{${key}}`, value),
    String(template || '')
  );
}

function StatusPill({ state }) {
  return <span className={`wa-status-pill is-${state}`}><i /> {STATUS_COPY[state]?.[0] || 'Sin conexión'}</span>;
}

export default function WhatsAppSettings() {
  const { user, setUser } = useAuth();
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState({ state: 'idle' });
  const [logs, setLogs] = useState([]);
  const [templateTab, setTemplateTab] = useState('reminder');
  const [testPhone, setTestPhone] = useState(user.phone || '');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState({ text: '', ok: false });

  const load = useCallback(async (quiet = false) => {
    try {
      const data = await api('/whatsapp');
      setConfig((current) => quiet && current ? current : data.settings);
      setStatus(data.status);
      setLogs(data.logs || []);
    } catch (error) {
      if (!quiet) setNotice({ text: error.message, ok: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!hasPlan(config?.productPlan, 'pro')) return undefined;
    const fastPolling = ['qr', 'connecting', 'pairing', 'reconnecting'].includes(status.state);
    const timer = window.setInterval(() => load(true), fastPolling ? 1800 : 6000);
    return () => window.clearInterval(timer);
  }, [config?.productPlan, load, status.state]);

  const isPro = hasPlan(config?.productPlan, 'pro');
  const connected = status.state === 'connected';
  const finalizingPair = status.state === 'pairing';
  const statusCopy = STATUS_COPY[status.state] || STATUS_COPY.idle;
  const templateKey = `${templateTab}Template`;
  const activeTemplate = templateTab === 'account' ? config?.accountTemplate : config?.[templateKey];
  const preview = useMemo(() => previewTemplate(activeTemplate), [activeTemplate]);

  const update = (key, value) => setConfig((current) => ({ ...current, [key]: value }));
  const flash = (text, ok = true) => {
    setNotice({ text, ok });
    window.setTimeout(() => setNotice({ text: '', ok: false }), 4200);
  };

  const save = async (event) => {
    event?.preventDefault();
    setBusy('save');
    try {
      const data = await api('/whatsapp/settings', { method: 'PUT', body: config });
      setConfig(data.settings);
      setUser({ ...user, waTemplate: data.settings.reminderTemplate, whatsapp: data.settings });
      flash('Configuración de WhatsApp guardada.');
    } catch (error) {
      flash(error.message, false);
    } finally {
      setBusy('');
    }
  };

  const connect = async () => {
    setBusy('connect');
    try {
      const data = await api('/whatsapp/connect', {
        method: 'POST',
        body: { refresh: ['qr', 'expired', 'error', 'logged_out', 'replaced'].includes(status.state) },
      });
      setStatus(data.status);
      flash(data.status.state === 'connected' ? 'WhatsApp conectado.' : 'Conexión iniciada. Escanea el QR cuando aparezca.');
      window.setTimeout(() => load(true), 900);
    } catch (error) {
      flash(error.message, false);
    } finally {
      setBusy('');
    }
  };

  const disconnect = async () => {
    if (!window.confirm('¿Desvincular este número? Será necesario escanear un QR para conectarlo de nuevo.')) return;
    setBusy('disconnect');
    try {
      const data = await api('/whatsapp/connection', { method: 'DELETE' });
      setStatus(data.status);
      flash('Número desvinculado correctamente.');
    } catch (error) {
      flash(error.message, false);
    } finally {
      setBusy('');
    }
  };

  const sendTest = async () => {
    setBusy('test');
    try {
      await api('/whatsapp/test', { method: 'POST', body: { phone: testPhone } });
      flash('Mensaje de prueba enviado desde tu número conectado.');
      await load(true);
    } catch (error) {
      flash(error.message, false);
    } finally {
      setBusy('');
    }
  };

  const runNow = async () => {
    if (!window.confirm('Se enviará un mensaje únicamente a los clientes que correspondan hoy según tus reglas. ¿Continuar?')) return;
    setBusy('run');
    try {
      const result = await api('/whatsapp/automation/run', { method: 'POST' });
      flash(`Proceso terminado: ${result.sent} enviados, ${result.skipped} omitidos y ${result.failed} fallidos.`);
      await load(true);
    } catch (error) {
      flash(error.message, false);
    } finally {
      setBusy('');
    }
  };

  if (!config) return <section className="settings-section wa-loading">Preparando tu centro de mensajes…</section>;

  return (
    <div className="wa-settings-stack">
      <section className={`wa-plan-banner ${isPro ? 'is-pro' : 'is-basic'}`}>
        <div className="wa-plan-icon">{isPro ? <IconZap size={22} /> : <IconChat size={22} />}</div>
        <div>
          <span className="wa-plan-kicker">WhatsApp · Plan {productPlanLabel(config?.productPlan)}</span>
          <h2>{isPro ? 'Mensajería desde tu propio número' : 'Mensajes asistidos, siempre bajo tu control'}</h2>
          <p>{isPro
            ? 'Vincula tu WhatsApp, entrega cuentas al instante y automatiza recordatorios con reglas seguras.'
            : 'StreamControl prepara cada mensaje y abre WhatsApp Web para que tú confirmes el envío.'}</p>
        </div>
        <span className={`product-plan-pill is-${isPro ? 'pro' : 'basic'}`}>{isPro ? 'PRO' : 'BÁSICO'}</span>
      </section>

      {isPro && (
        <section className="settings-section wa-connection-card">
          <div className="settings-section-head">
            <span><IconQrCode size={19} /></span>
            <div><h2>Conexión del canal</h2><p>Este número será el remitente de todos los mensajes Pro.</p></div>
            <StatusPill state={status.state} />
          </div>

          {!config.runtimeAvailable ? (
            <div className="wa-runtime-warning">
              <IconShield size={19} />
              <div><strong>Se necesita un servidor persistente</strong><p>Baileys mantiene una conexión activa. Ejecuta el backend en un servidor con Docker y Coolify; las funciones serverless no conservan este canal.</p></div>
            </div>
          ) : (
            <div className={`wa-connection-body is-${status.state}`}>
              {status.state === 'qr' && status.qr ? (
                <div className="wa-qr-shell">
                  <QRCodeSVG value={status.qr} size={210} level="M" includeMargin />
                  <span>El QR se renueva automáticamente</span>
                </div>
              ) : (
                <div className="wa-device-visual">
                  <span className="wa-device-orbit"><IconChat size={32} /></span>
                  {connected && <i><IconCheck size={16} /></i>}
                </div>
              )}
              <div className="wa-connection-copy">
                <span className="eyebrow">{statusCopy[0]}</span>
                <h3>{connected ? (status.name || 'WhatsApp conectado') : statusCopy[1]}</h3>
                {connected ? (
                  <>
                    <p>+{status.phone} · conectado {status.connectedAt ? fmtDate(status.connectedAt) : 'ahora'}</p>
                    <div className="wa-connection-actions">
                      <button type="button" className="btn-ghost" onClick={connect} disabled={!!busy}><IconRefresh size={15} /> Reconectar</button>
                      <button type="button" className="wa-disconnect-button" onClick={disconnect} disabled={!!busy}><IconX size={15} /> Desvincular</button>
                    </div>
                  </>
                ) : (
                  <>
                    <p>{status.message || statusCopy[1]}</p>
                    <button type="button" className="btn-primary" onClick={connect} disabled={!!busy || finalizingPair}>
                      <IconQrCode size={16} /> {busy === 'connect'
                        ? 'Preparando…'
                        : finalizingPair
                          ? 'Finalizando vínculo…'
                          : status.state === 'qr'
                            ? 'Generar otro QR'
                            : 'Vincular WhatsApp'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
          <div className="wa-unofficial-note"><IconShield size={15} /><span>Conexión no oficial mediante Baileys. Úsala solo con clientes que aceptaron recibir mensajes; evita spam y envíos masivos.</span></div>
        </section>
      )}

      <form onSubmit={save} className="settings-section wa-templates-card">
        <div className="settings-section-head">
          <span><IconChat size={18} /></span>
          <div><h2>Plantillas de mensajes</h2><p>Personaliza el tono y deja que StreamControl complete los datos.</p></div>
        </div>
        <div className="wa-template-tabs" role="tablist">
          <button type="button" className={templateTab === 'reminder' ? 'is-active' : ''} onClick={() => setTemplateTab('reminder')}>Próximo pago</button>
          <button type="button" className={templateTab === 'renewal' ? 'is-active' : ''} onClick={() => setTemplateTab('renewal')}>Renovación</button>
          <button type="button" className={templateTab === 'overdue' ? 'is-active' : ''} onClick={() => setTemplateTab('overdue')}>Pago vencido</button>
          <button type="button" className={templateTab === 'account' ? 'is-active' : ''} onClick={() => setTemplateTab('account')}>Entrega de cuenta</button>
        </div>
        {templateTab === 'renewal' && (
          <p className="wa-template-hint">Este es el mensaje que se envía al tocar el botón <strong>Renovar</strong> en cada cliente. StreamControl completa el servicio, la fecha de vencimiento y el monto de la próxima suscripción por vencer.</p>
        )}
        <div className="wa-template-editor-grid">
          <div>
            <label className="field-label">Mensaje
              <textarea
                className="input mt-2"
                rows="8"
                value={activeTemplate || ''}
                onChange={(event) => update(templateTab === 'account' ? 'accountTemplate' : templateKey, event.target.value)}
              />
            </label>
            <div className="template-variables">
              <span>{'{cliente}'}</span><span>{'{servicio}'}</span><span>{'{fecha}'}</span><span>{'{monto}'}</span>
              {templateTab === 'account' && <><span>{'{correo}'}</span><span>{'{contrasena}'}</span><span>{'{perfil}'}</span><span>{'{pin}'}</span></>}
            </div>
          </div>
          <div className="wa-phone-preview">
            <div className="wa-phone-preview-head"><IconChat size={15} /><span>{user.businessName || user.name}</span></div>
            <p>{preview}</p>
            <time>09:41 ✓✓</time>
          </div>
        </div>
        {!isPro && (
          <>
            <div className="wa-basic-explainer"><IconSend size={18} /><div><strong>¿Cómo funciona en Básico?</strong><p>Los botones en Recordatorios, Clientes y Suscripciones abren WhatsApp Web con esta plantilla lista. Tú revisas y presionas enviar.</p></div></div>
            <label className="field-label">Código de país predeterminado
              <div className="wa-country-input mt-2"><span>+</span><input value={config.countryCode || '506'} onChange={(event) => update('countryCode', event.target.value.replace(/\D/g, '').slice(0, 4))} /></div>
              <small className="wa-template-hint">Se antepone a los números locales de 8 dígitos al abrir WhatsApp.</small>
            </label>
          </>
        )}
        {!isPro && <button className="btn-primary settings-save" disabled={busy === 'save'}>{busy === 'save' ? 'Guardando…' : 'Guardar plantillas'}</button>}
      </form>

      {isPro && (
        <section className="settings-section wa-automation-card">
          <div className="settings-section-head">
            <span><IconClock size={18} /></span>
            <div><h2>Reglas de automatización</h2><p>Define a quién se escribe, cuándo y con qué frecuencia.</p></div>
            <label className="wa-switch">
              <input type="checkbox" checked={!!config.automationEnabled} onChange={(event) => update('automationEnabled', event.target.checked)} />
              <span /><b>{config.automationEnabled ? 'Activo' : 'Pausado'}</b>
            </label>
          </div>
          <div className="wa-rule-grid">
            <div className="wa-rule-block">
              <span className="wa-rule-label">Avisar antes del vencimiento</span>
              <div className="wa-day-picker">
                {[7, 3, 1, 0].map((day) => {
                  const active = config.reminderDays?.includes(day);
                  return <button key={day} type="button" className={active ? 'is-active' : ''} onClick={() => update('reminderDays', active ? config.reminderDays.filter((value) => value !== day) : [...(config.reminderDays || []), day])}>{day === 0 ? 'El día' : `${day}d`}</button>;
                })}
              </div>
            </div>
            <label className="field-label">Hora de envío
              <input className="input mt-2" type="time" value={config.sendTime || '09:00'} onChange={(event) => update('sendTime', event.target.value)} />
            </label>
            <label className="field-label">Zona horaria
              <select className="input mt-2" value={config.timezone || 'America/Costa_Rica'} onChange={(event) => update('timezone', event.target.value)}>
                <option value="America/Costa_Rica">Costa Rica</option>
                <option value="America/Guatemala">Guatemala</option>
                <option value="America/Mexico_City">Ciudad de México</option>
                <option value="America/Bogota">Bogotá</option>
                <option value="America/Lima">Lima</option>
                <option value="America/Panama">Panamá</option>
              </select>
            </label>
            <label className="field-label">Código de país predeterminado
              <div className="wa-country-input mt-2"><span>+</span><input value={config.countryCode || '506'} onChange={(event) => update('countryCode', event.target.value.replace(/\D/g, '').slice(0, 4))} /></div>
            </label>
          </div>
          <div className="wa-check-list">
            <label><input type="checkbox" checked={!!config.sendAccountOnCreate} onChange={(event) => update('sendAccountOnCreate', event.target.checked)} /><span><strong>Entregar cuentas al crearlas</strong><small>Envía credenciales y PIN automáticamente al nuevo cliente.</small></span></label>
            <label><input type="checkbox" checked={!!config.overdueEnabled} onChange={(event) => update('overdueEnabled', event.target.checked)} /><span><strong>Dar seguimiento a vencidos</strong><small>Reintenta cada <select value={config.overdueEveryDays || 3} onChange={(event) => update('overdueEveryDays', Number(event.target.value))}><option value="1">1 día</option><option value="3">3 días</option><option value="7">7 días</option></select>.</small></span></label>
            <label className="is-consent"><input type="checkbox" checked={!!config.consentAccepted} onChange={(event) => update('consentAccepted', event.target.checked)} /><span><strong>Uso responsable y con consentimiento</strong><small>Confirmo que mis clientes aceptaron recibir estos mensajes y que no usaré envíos masivos.</small></span></label>
          </div>
          <div className="wa-automation-actions">
            <button type="button" className="btn-ghost" onClick={runNow} disabled={!connected || !!busy}><IconZap size={16} /> {busy === 'run' ? 'Procesando…' : 'Ejecutar ahora'}</button>
            <button type="button" className="btn-primary" onClick={save} disabled={busy === 'save'}><IconCheck size={16} /> {busy === 'save' ? 'Guardando…' : 'Guardar automatización'}</button>
          </div>
        </section>
      )}

      {isPro && (
        <section className="settings-section wa-test-history">
          <div className="wa-test-panel">
            <div><span className="eyebrow">Prueba del canal</span><h3>Comprueba el envío</h3><p>Enviaremos un único mensaje de confirmación.</p></div>
            <div className="wa-test-controls"><input className="input" value={testPhone} onChange={(event) => setTestPhone(event.target.value)} placeholder="WhatsApp de prueba" /><button type="button" className="btn-primary" onClick={sendTest} disabled={!connected || !!busy}><IconSend size={16} /> {busy === 'test' ? 'Enviando…' : 'Enviar prueba'}</button></div>
          </div>
          <div className="wa-history-panel">
            <div className="wa-history-head"><div><span className="eyebrow">Actividad reciente</span><h3>Últimos mensajes</h3></div><button type="button" onClick={() => load(true)} aria-label="Actualizar historial"><IconRefresh size={16} /></button></div>
            {logs.length ? logs.map((log) => (
              <div className="wa-log-row" key={log._id}>
                <span className={`wa-log-state is-${log.status}`}>{log.status === 'sent' ? <IconCheck size={13} /> : <IconX size={13} />}</span>
                <div><strong>{log.clientId?.name || TYPE_LABEL[log.type] || 'WhatsApp'}</strong><small>{TYPE_LABEL[log.type] || log.type} · {fmtDate(log.sentAt || log.createdAt)}</small></div>
                <b>{log.status === 'sent' ? 'Enviado' : log.status === 'pending' ? 'En cola' : 'Falló'}</b>
              </div>
            )) : <p className="wa-empty-history">Los envíos aparecerán aquí.</p>}
          </div>
        </section>
      )}

      {notice.text && <div className={`wa-floating-notice ${notice.ok ? 'is-success' : 'is-error'}`}>{notice.ok ? <IconCheck size={16} /> : <IconX size={16} />}{notice.text}</div>}
    </div>
  );
}
